import Reflux from "reflux";
import _ from "lodash";
import AppConfig from "../config/app";
import AppConstants from "../constants/AppConstants";
import DesignChooserConstants from "../constants/DesignChooserConstants";
import DesignBrowserStore from "./DesignBrowserStore";
import { getUserId } from "../hooks/query/useAccount";
import SiteDesignStore from "./SiteDesignStore";
import DesignChooserActions from "../actions/DesignChooserActions";
import UIActions from "../actions/UIActions";
import LoaderActions from "../actions/LoaderActions";
import SiteActions from "../actions/SiteActions";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import { getSiteKey } from "../hooks/query/useSite";
import { userSitesQuery } from "../hooks/query/useSites";
import { userLibraryDesignsQuery } from "../hooks/query/useUserLibraryDesigns";
import { showitLibraryDesignsQuery } from "../hooks/query/useShowitLibraryDesigns";
import AdminActions from "../actions/AdminActions";
import { queryClient } from "../utils/QueryUtils";
import { updateProcessingState } from "../utils/ProcessingStateUtils";
import DesignVersionUtils from "../utils/DesignVersionUtils";
import {
  MIGRATION_TYPE_BACKUP,
  MIGRATION_TYPE_PRIMARY,
  MIGRATION_TYPE_SHARED,
} from "../constants/MigrationConstants";

const {
  LAYOUT_DESKTOP,
  DESIGN_TYPE_BLOCK,
  DESIGN_TYPE_SITE,
  DESIGN_TYPE_PAGE,
  DESIGN_TYPE_WP_PAGE,
  DESIGN_TYPE_SITE_BLOCK,
  MODAL_DESIGN_CHOOSER,
  BLOCK_PROP_PARENT_BLOCK_ID,
  ITEM_TYPE_PAGE_FOLDER,
  ITEM_TYPE_PAGE,
} = AppConstants;
const { TAB_CURRENT_SITE, TAB_USER_SITES, TAB_USER_LIBRARY, TAB_PLATFORM_LIBRARY } =
  DesignChooserConstants;

const designListModel = {
  items: [],
  error: false,
  loading: false,
  selected: {},
  currentTheme: null,
  showSiteBlocks: true,
  showPageBlocks: true,
};

const initialState = {
  title: "Design Chooser",
  actionLabel: "Add",
  actionEnabled: false,
  previewLayout: LAYOUT_DESKTOP,
  designType: DESIGN_TYPE_BLOCK,
  tab: TAB_CURRENT_SITE,
  listOpen: true,
  useCurrentTheme: false,
  designList: null,
  preview: null,
  busy: false,
  error: false,
  showTabs: true,
  restoreMode: false,
};

const DesignChooserStore = Reflux.createStore({
  storeName: 'DesignChooserStore',
  listenables: [DesignChooserActions, LoaderActions, AdminActions, SiteActions],

  init() {
    this.siteListStoreListener = null;

    this.loadingItem = null;
    this.sessionId = null; //an timestamp that is generated when the chooser is first opened

    var list = _.cloneDeep(designListModel);

    this.state = _.cloneDeep(initialState);
    this.state.designList = list;
  },

  onSelectTab(data) {
    if (this.state.busy || this.loadingItem) {
      return;
    }

    if (this.state.tab != data.tab) {
      this.state.tab = data.tab;
      this.state.preview = null;

      this._loadDesignList();

      this.trigger(this.state);
    }
  },

  _showLoadingOverlay(message) {
    // prevents stacking loading screens when selecting items quickly
    this._resetLoadingOverlay();
    this._clearLoadingOverlayTimer();
    this.state.timer = setTimeout(() => {
      updateProcessingState({ processing: true, overlay: true, message: message || "" });
    }, 300);
  },

  _hideLoadingOverlay() {
    this._clearLoadingOverlayTimer();
    updateProcessingState({ processing: false, message: "", overlay: false, delay: 400 });
  },

  _clearLoadingOverlayTimer() {
    this.state?.timer && clearTimeout(this.state.timer);
  },

  _showLoadingOverlayError(errorItem) {
    this._clearLoadingOverlayTimer();
    updateProcessingState({ processing: false, overlay: true, error: errorItem || true });
  },

  _resetLoadingOverlay() {
    updateProcessingState({ reset: true });
  },

  onSelectItem(data) {
    if (this.state.busy || this.loadingItem) {
      return;
    }

    const { item } = data;
    const { selected } = this.state.designList;

    switch (item.type) {
      case DESIGN_TYPE_BLOCK:
        if (_.get(selected, "block_id") != item.id) {
          selected.block_id = item.id;
          this._setPreviewData(DESIGN_TYPE_BLOCK, selected.block_id);
        } else {
          _.unset(selected, "block_id");
          this._setPreviewData(DESIGN_TYPE_PAGE, selected.page_id);
        }
        break;
      case DESIGN_TYPE_SITE:
        if (_.get(selected, "site_id") != item.id) {
          this._showLoadingOverlay();
          _.unset(selected, "page_id");
          _.unset(selected, "site_block_id");
          _.unset(selected, "block_id");
          selected.site_id = item.id;
          this._loadDesign(item);
        } else {
          _.unset(selected, "block_id");
          _.unset(selected, "site_block_id");
          _.unset(selected, "page_id");
          this.state.preview = null;
        }
        break;
      case DESIGN_TYPE_PAGE:
      case DESIGN_TYPE_WP_PAGE:
        if (_.get(selected, "page_id") != item.id) {
          this._showLoadingOverlay();
          _.unset(selected, "block_id");
          _.unset(selected, "site_block_id");
          selected.page_id = item.id;
          this._loadDesignPage(item);
        } else {
          _.unset(selected, "page_id");
          _.unset(selected, "block_id");
          _.unset(selected, "site_block_id");
          this._setPreviewData(DESIGN_TYPE_PAGE, selected.page_id);
        }
        break;
      case DESIGN_TYPE_SITE_BLOCK:
        if (_.get(selected, "site_block_id") != item.id) {
          _.unset(selected, "block_id");
          _.unset(selected, "page_id");
          selected.site_block_id = item.id;
          this._setPreviewData(DESIGN_TYPE_SITE_BLOCK, selected.site_block_id);
        } else {
          _.unset(selected, "block_id");
          _.unset(selected, "page_id");
          _.unset(selected, "site_block_id");
        }
        break;
      default:
    }

    this._calculateSelectedType(item);
    this._updateActionState();

    this.trigger(this.state);
  },

  onSelectLayout(layout) {
    this.state.previewLayout = layout;

    this.trigger(this.state);
  },

  _calculateSelectedType(clicked) {
    const selected = this.state.designList.selected;

    // handle unselecting of items in the tree
    if (
      clicked.type === DESIGN_TYPE_BLOCK &&
      selected.page_id &&
      selected.site_id &&
      !selected.block_id
    ) {
      selected.type = this._getPageType(selected.page_id);
    } else if (
      clicked.type === DESIGN_TYPE_PAGE &&
      !selected.page_id &&
      selected.site_id &&
      !selected.block_id
    ) {
      selected.type = DESIGN_TYPE_SITE;
    } else if (
      clicked.type === DESIGN_TYPE_WP_PAGE &&
      !selected.page_id &&
      selected.site_id &&
      !selected.block_id
    ) {
      selected.type = DESIGN_TYPE_SITE;
    } else if (
      clicked.type === DESIGN_TYPE_SITE_BLOCK &&
      !selected.page_id &&
      selected.site_id &&
      !selected.site_block_id
    ) {
      selected.type = DESIGN_TYPE_SITE;
    } else {
      selected.type = clicked.type;
    }

    selected.readableType = this._calculateFriendlyType(selected.type);
  },

  _calculateFriendlyType(nonReadableType) {
    switch (nonReadableType) {
      case DESIGN_TYPE_SITE:
        return "SITE";
      case DESIGN_TYPE_SITE_BLOCK:
        return "SITE CANVAS";
      case DESIGN_TYPE_PAGE:
        return "PAGE";
      case DESIGN_TYPE_BLOCK:
        return "CANVAS";
      case DESIGN_TYPE_WP_PAGE:
        return "BLOG TEMPLATE";
    }
  },

  _getPageType(id) {
    const site = this.state.designList.items.filter(
      (siteEntry) => siteEntry.id == this.state.designList.selected.site_id
    );

    const pages = site[0].pages;
    if (pages.find((page) => page.id == id)) {
      return DESIGN_TYPE_PAGE;
    }

    const wpPages = site[0].wppages;
    if (wpPages.find((page) => page.id == id)) {
      return DESIGN_TYPE_WP_PAGE;
    }
  },

  _restore_onChooseItem() {
    const selected = this.state.designList.selected;
    const selection = {};

    if (_.has(selected, "site_block_id")) {
      selection.type = DESIGN_TYPE_SITE_BLOCK;
      selection.id = _.get(selected, "site_block_id");
    } else if (_.has(selected, "block_id")) {
      selection.type = DESIGN_TYPE_BLOCK;
      selection.id = _.get(selected, "block_id");
    } else if (_.has(selected, "page_id")) {
      selection.type = DESIGN_TYPE_PAGE;
      selection.id = _.get(selected, "page_id");
    } else if (_.has(selected, "site_id")) {
      selection.type = DESIGN_TYPE_SITE;
      selection.id = _.get(selected, "site_id");
    }

    this._restore(selection, DesignBrowserStore.getDesignItemData(selection));
  },

  onChooseItem() {
    if (this.state.restoreMode) {
      return this._restore_onChooseItem();
    }

    const { designType } = this.state;
    const { selected } = this.state.designList;

    const selection = {};

    switch (designType) {
      case DESIGN_TYPE_BLOCK:
      case DESIGN_TYPE_SITE_BLOCK:
        if (_.has(selected, "block_id")) {
          selection.type = DESIGN_TYPE_BLOCK;
          selection.id = _.get(selected, "block_id");
        } else if (_.has(selected, "site_block_id")) {
          selection.type = DESIGN_TYPE_SITE_BLOCK;
          selection.id = _.get(selected, "site_block_id");
        }
        break;
      case DESIGN_TYPE_SITE:
        if (_.has(selected, "site_id")) {
          selection.type = DESIGN_TYPE_SITE;
          selection.id = _.get(selected, "site_id");
        }
        break;
      case DESIGN_TYPE_PAGE:
      case DESIGN_TYPE_WP_PAGE:
        if (_.has(selected, "page_id")) {
          selection.type = DESIGN_TYPE_PAGE;
          selection.id = _.get(selected, "page_id");
        }
        break;
      default:
    }

    const data = DesignBrowserStore.getDesignItemData(selection);

    if (this.state.restoreMode) {
      return this._restore(selected, data);
    }

    data.targetDesignType = designType; // determines how the design item should be added to the page or site
    data.applyTheme = this.state.useCurrentTheme; // setting true causes theme to be merged into design item

    DocumentStore.dispatch({ type: COMMANDS.ADD_DESIGN_ITEM, payload: data });

    DesignChooserActions.closeDialog();
  },

  _restore(selection, designData) {
    switch (selection.type) {
      case DESIGN_TYPE_PAGE:
        AdminActions.restorePage(selection, designData);
        break;
      case DESIGN_TYPE_SITE_BLOCK:
        AdminActions.restoreSiteBlock(selection, designData);
        break;
      case DESIGN_TYPE_BLOCK:
        selection.pageId = this.state.designList.selected.page_id;
        AdminActions.restoreCanvas(selection, designData);
        break;
      default:
        //site
        const site = selection.id.split("/")[0];

        const payload = {
          site_key: site,
          dest_key: site,
          src_key: this.state.designList.selected.site_id,
        };

        AdminActions.restoreSite(payload);
        break;
    }
  },

  onToggleList() {
    this.state.listOpen = !this.state.listOpen;
    this.trigger(this.state);
  },

  onToggleTheme(value) {
    this.state.useCurrentTheme = value;
    this.trigger(this.state);
  },

  onCloseDialog() {
    this._reset();

    UIActions.modalClose(MODAL_DESIGN_CHOOSER);
  },

  onOpenDialog(config) {
    this.init();

    this.sessionId = Date.now();

    this.bucket = AppConfig.designsBucket;

    this.state = _.merge(this.state, config);

    if (config.restoreMode) {
      this.bucket = AppConfig.designsBackupBucket;
      this.state.title = "Choose A Restore Point";
      this.state.actionLabel = "Restore";
    }

    this._loadDesignList();

    UIActions.modalOpen(MODAL_DESIGN_CHOOSER, {
      onHide: DesignChooserActions.closeDialog,
    });
  },

  _loadDesignList() {
    var siteDesign = SiteDesignStore.getDesign();

    this.state.designList = _.cloneDeep(designListModel);
    this.state.designList.currentTheme = siteDesign.theme;
    this.state.useCurrentTheme = false;

    switch (this.state.designType) {
      case DESIGN_TYPE_PAGE:
      case DESIGN_TYPE_WP_PAGE:
        this.state.designList.showSiteBlocks = false;
        this.state.designList.showPageBlocks = false;
        break;
    }

    if (this.state.restoreMode) {
      AdminActions.getSiteBackups(getSiteKey());
    } else {
      switch (this.state.tab) {
        case TAB_CURRENT_SITE:
          this.bucket = AppConfig.designsBucket;
          this.state.useCurrentTheme = true;
          this._loadCurrentSite();
          break;
        case TAB_USER_SITES:
          this.bucket = AppConfig.designsBucket;
          this._loadUserSites();
          break;
        case TAB_USER_LIBRARY:
          this.bucket = AppConfig.sitesBucket;
          this._loadUserSiteLibrary();
          break;
        case TAB_PLATFORM_LIBRARY:
          this.bucket = AppConfig.sitesBucket;
          this._loadPlatformLibrary();
          break;
      }
    }

    this.trigger(this.state);
  },

  async _loadUserSites() {
    this.state.designList.loading = true;

    try {
      const data = await queryClient.fetchQuery(userSitesQuery);
      this._updateUserSites(data);
    } catch (error) {
      this.state.designList.loading = false;
      this.state.designList.error = error.message;
      this.trigger(this.state);
    }
  },

  _updateUserSites(siteList) {
    var designList = this.state.designList;

    this._updateStateFromList(siteList);

    var items = siteList.sites;
    var designs = [];
    items.forEach(function (site) {
      designs.push({
        name: site.name,
        id: site.site_design_id,
        key: site.key,
        type: DESIGN_TYPE_SITE,
      });
    });

    designList.items = designs;
    this.state.designList.loading = false;
    this.state.designList.error = false;
    this.trigger(this.state);
  },

  _loadCurrentSite() {
    const designList = this.state.designList;
    const siteDesign = SiteDesignStore.getDesign();
    const designData = siteDesign.data.toJS();

    DesignBrowserStore.setDesign(designData);
    const designInfo = DesignBrowserStore.getDesignInfo();

    const designItem = {
      id: designInfo.id,
      name: designInfo.name,
      key: designInfo.id,
    };

    const allPagesWithFolders = siteDesign.getPageList({ withFolders: true });
    const { regularPages, wpPages } = this._separatePagesByType(allPagesWithFolders);

    designItem.pages = this._buildPageItems(designItem, regularPages, DESIGN_TYPE_PAGE);
    designItem.wppages = this._buildPageItems(designItem, wpPages, DESIGN_TYPE_WP_PAGE);
    designItem.blocks = this._buildSiteBlockItems(designItem, designInfo.blocks);
    designItem.theme = _.get(designInfo, "theme");

    _.set(this.state.designList.selected, "site_id", designItem.id);
    designList.items.push(designItem);
  },

  /**
   * Separates pages into regular and WordPress arrays, preserving folder structure
   * @param {Object} allPages - All pages with folders from SiteDesign
   * @returns {Object} Object containing regularPages and wpPages arrays
   */
  _separatePagesByType(allPages) {
    const regularPages = [];
    const wpPages = [];

    allPages.forEach((page) => {
      const pageData = page.toJS();
      const targetArray = pageData.wp ? wpPages : regularPages;
      targetArray.push(pageData);
    });

    return { regularPages, wpPages };
  },

  async _loadUserSiteLibrary() {
    this.state.designList.loading = true;
    const userId = getUserId();
    const designType = DESIGN_TYPE_SITE;

    try {
      const data = await queryClient.fetchQuery(userLibraryDesignsQuery(userId, designType));
      this._updateUserLibrarySites(data);
    } catch (error) {
      this.state.designList.loading = false;
      this.state.designList.error = error.message;
      this.trigger(this.state);
    }
  },

  _updateUserLibrarySites(siteList) {
    var designList = this.state.designList;

    var items = siteList.designs;
    var designs = [];
    items.forEach(function (site) {
      designs.push({
        name: site.name,
        id: site.design_id,
        key: site.pub_key,
        type: DESIGN_TYPE_SITE,
      });
    });

    designList.items = designs;
    this.state.designList.loading = false;
    this.state.designList.error = false;
    this.trigger(this.state);
  },

  async _loadPlatformLibrary() {
    this.state.designList.loading = true;
    try {
      const data = await queryClient.fetchQuery(showitLibraryDesignsQuery);
      this._updatePlatformSites(data);
    } catch (error) {
      this.state.designList.loading = false;
      this.state.designList.error = error.message;
      this.trigger(this.state);
    }
  },

  _updatePlatformSites(siteList) {
    var designList = this.state.designList;

    var items = siteList.sites;
    var designs = [];
    items.forEach(function (site) {
      designs.push({
        name: site.name,
        id: site.site_design_id,
        key: site.key,
        pub_id: site.pub_id,
        type: DESIGN_TYPE_SITE,
      });
    });

    designList.items = designs;
    this.state.designList.loading = false;
    this.state.designList.error = false;
    this.trigger(this.state);
  },

  _updateStateFromList(siteList) {
    this.state.busy = siteList.busy;

    this.state.designList.loading = siteList.busy;
    this.state.designList.error = siteList.error;
  },

  _resetSiteListStoreListener() {
    if (this.siteListStoreListener != null) {
      this.siteListStoreListener.stop();
      this.siteListStoreListener = null;
    }
  },

  _loadDesign(designItem) {
    this.loadingItem = designItem;
    this.loadingItem.loading = true;
    const url = `${this.bucket}/${designItem.key}/site.json`;

    LoaderActions.loadJSON({ url, key: designItem.key });
  },

  _loadDesignPage(pageItem) {
    this.loadingItem = pageItem;
    this.loadingItem.loading = true;

    const url = `${this.bucket}/${pageItem.key}.json`;

    LoaderActions.loadJSON({ url });
  },

  onLoadJSON() {
    this.state.busy = true;
    this.trigger(this.state);
  },

  onLoadJSONComplete(result, key) {
    const isCorrectDesignVersion = DesignVersionUtils.isCorrectVersion(result?.data?.version);

    if (!isCorrectDesignVersion) {
      const { backup_status, key } = this.loadingItem;
      const isShared = key.includes("/");
      const migrationType =
        backup_status !== undefined
          ? MIGRATION_TYPE_BACKUP
          : isShared
          ? MIGRATION_TYPE_SHARED
          : MIGRATION_TYPE_PRIMARY;

      const migrationConfig = {
        key,
        migrationType,
      };
      // design was migrated, but is still on the wrong version, force migration
      if (this.hadSuccessfulMigration) {
        migrationConfig.force = true;
        this.hadSuccessfulMigration = false;
      }

      DesignVersionUtils.migrateDesign(migrationConfig)
        .then(({ isSuccess = false }) => {
          if (isSuccess) {
            this.hadSuccessfulMigration = true;
          }
          this._loadDesign(this.loadingItem);
        })
        .catch(this.onLoadJSONFail);
      return;
    } else if (this.hadSuccessfulMigration) {
      this.hadSuccessfulMigration = false;
    }

    this.state.busy = false;

    var type = this.loadingItem.type;

    //result returns data and etag
    var json = _.get(result, "data");

    switch (type) {
      case DESIGN_TYPE_SITE:
        this._completeDesignLoad(this.loadingItem, json);
        break;
      case DESIGN_TYPE_PAGE:
      case DESIGN_TYPE_WP_PAGE:
        this._completePageLoad(this.loadingItem, json);
        break;
    }

    this.loadingItem.loading = false;
    this.loadingItem = null;

    this.trigger(this.state);
  },

  onLoadJSONFail(err) {
    const { type } = this.loadingItem;

    this.state.busy = false;
    this.loadingItem.loading = false;
    this.loadingItem = null;

    this._showLoadingOverlayError(type);
    this.trigger(this.state);
  },

  _completePageLoad(pageItem, pageData) {
    var id = pageItem.id;

    var pageId = _.get(pageData, "id");

    if (pageId == id) {
      DesignBrowserStore.setPage(pageData);
      var pageInfo = DesignBrowserStore.getPageInfo();

      pageItem.blocks = this._buildPageBlockItems(pageItem, pageInfo.blocks);

      this._setPreviewData(DESIGN_TYPE_PAGE, pageId);
    }
    this._hideLoadingOverlay();
  },

  /**
   * Completes the design load and sets the design item and design info
   * @param {Object} designItem - The design item containing id and key
   * @param {Object} designData - The design data containing items and folders
   */
  _completeDesignLoad(designItem, designData) {
    var key = designItem.key;
    var designId = _.get(designData, "id");

    if (_.startsWith(key, designId)) {
      DesignBrowserStore.setDesign(designData);
      var designInfo = DesignBrowserStore.getDesignInfo();

      if (designData.folders && Object.keys(designData.folders).length > 0) {
        const { regularPages, wpPages } = this._reconstructFolderStructureFromRaw(designData);

        designItem.pages = this._buildPageItems(designItem, regularPages, DESIGN_TYPE_PAGE);
        designItem.wppages = this._buildPageItems(designItem, wpPages, DESIGN_TYPE_WP_PAGE);
      } else {
        designItem.pages = this._buildPageItems(designItem, designInfo.pages, DESIGN_TYPE_PAGE);
        designItem.wppages = this._buildPageItems(
          designItem,
          designInfo.wppages,
          DESIGN_TYPE_WP_PAGE
        );
      }

      designItem.blocks = this._buildSiteBlockItems(designItem, designInfo.blocks);
      designItem.theme = _.get(designInfo, "theme");
      this._hideLoadingOverlay();
    }
  },

  /**
   * Analyzes folder by page type
   * @param {Object} items - The items to analyze
   * @returns {Object} Object containing regularPages and wpPages ARRAYs with folder structure
   */
  _analyzePageTypes(items) {
    const folderPageTypes = Object.values(items).reduce((acc, item) => {
      if (item.pId) {
        const folderId = item.pId;
        acc[folderId] ??= { hasWpPages: false, hasRegularPages: false };
        const folder = acc[folderId];
        folder[item.wp ? "hasWpPages" : "hasRegularPages"] = true;
      }
      return acc;
    }, {});

    return folderPageTypes;
  },

  /**
   * Separates folders to arrays based on the page type
   * @param {Object} folders - The folders to separate
   * @param {Object} items - The page items (with pId properties) to analyze for folder content
   * @returns {Object} Object containing regularPages and wpPages arrays
   */
  _separateFoldersByPageType(folders, items) {
    const regularPages = [];
    const wpPages = [];
  
    const folderPageTypes = this._analyzePageTypes(items);
    Object.values(folders).forEach((folder) => {
      const content = folderPageTypes[folder.id];
      const hasWpPages = content?.hasWpPages ?? !!folder.wp;
      const hasRegularPages = content?.hasRegularPages ?? !folder.wp;
  
      const folderData = {
        id: folder.id,
        name: folder.name,
        slug: folder.slug,
        index: folder.index,
        type: ITEM_TYPE_PAGE_FOLDER,
        wp: hasWpPages,
      };
  
      if (hasWpPages) {
        wpPages.push(folderData);
      }
  
      if (hasRegularPages) {
        regularPages.push({ ...folderData, wp: false });
      }
    });
  
    return { regularPages, wpPages };
  },

  /**
   * Separates pages to arrays based on the page type
   * @param {Object} items - The items to separate
   * @param {Array} pageOrder - The page order to use
   * @returns {Object} Object containing regularPages and wpPages arrays
   */
  _separatePagesByPageType(items, pageOrder = []) {
    const regularPages = [];
    const wpPages = [];

    const pageIndexMap = new Map();
    pageOrder.forEach((pageId, index) => {
      pageIndexMap.set(pageId, index);
    });

    Object.values(items).forEach((item) => {
      const pageData = {
        id: item.id,
        name: item.name,
        slug: item.slug,
        publishable: item.publishable,
        noPublish: item.noPublish,
        home: item.home,
        wp: !!item.wp,
        pId: item.pId,
        type: ITEM_TYPE_PAGE,
        index: pageIndexMap.get(item.id) ?? undefined,
      };

      (pageData.wp ? wpPages : regularPages).push(pageData);
    });

    return { regularPages, wpPages };
  },

  /**
   * Sorts pages by index
   * @param {Array} pages - The pages to sort
   * @returns {Array} The sorted pages
   */
  _sortPagesByIndex(pages) {
    return pages.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  },

  /**
   * Reindexes items with insertions
   * @param {Array} sortedPages - The pages to reindex
   * @param {Array} folders - The folders to reindex
   * @param {Function} getBaseIndex - The function to get the base index
   * @returns {Map} The reindexed items
   */
  _reindexItemsWithInsertions(sortedPages, folders, getBaseIndex, useLoopIndexInComparison = false) {
    const resultMap = new Map();
    let folderIndex = 0;

    sortedPages.forEach((page, i) => {
      if (!page.id) return;

      const baseIndex = getBaseIndex(page, i);

      while (folderIndex < folders.length) {
        const folder = folders[folderIndex];
        const comparisonValue = useLoopIndexInComparison ? baseIndex + i : baseIndex + folderIndex;
        if (folder.index !== undefined && folder.index <= comparisonValue) {
          folderIndex++;
        } else {
          break;
        }
      }

      resultMap.set(page.id, {
        ...page,
        index: baseIndex + folderIndex
      });
    });

    return resultMap;
  },

  /**
   * Converts page indices from the pages-only indexing system into the full, combined index system that includes folders.
   *
   * @param {Array} pages - The list of pages using the pages-only index values.
   * @param {Array} sortedFolders - All folders, already sorted by their position in the combined index system.
   * @returns {Array} A new list of pages with their indices adjusted to match the combined system.
   */
  _convertRegularPageIndicesToCombined(pages, sortedFolders) {
    const pagesWithIndices = pages.filter(p => p.index !== undefined);
    if (pagesWithIndices.length === 0) {
      return pages.map(p => p);
    }
    
    const sortedPages = this._sortPagesByIndex([...pagesWithIndices]);
    const getBaseIndex = (page) => page.index;
    const result = this._reindexItemsWithInsertions(sortedPages, sortedFolders, getBaseIndex);
    
    return pages.map(page => {
      return page.index === undefined || !page.id ? page : result.get(page.id) ?? page;
    });
  },

  /**
   * Converts WP page indices from the pages-only indexing system into the full, combined index system that includes folders.
   *
   * @param {Array} pages - The list of WP pages using the pages-only index values.
   * @param {Array} sortedFolders - All folders, already sorted by their position in the combined index system.
   * @param {Array} convertedRegularPages - The list of regular pages, already converted to the combined index system.
   * @param {Array} sortedRegularFolders - The list of regular folders, already sorted by their position in the combined index system.
   * @returns {Array} A new list of WP pages with their indices adjusted to match the combined system.
   */
  _convertWpPageIndicesToCombined(pages, sortedFolders, convertedRegularPages, sortedRegularFolders) {
    const regularItems = [...convertedRegularPages, ...sortedRegularFolders];
    const regularOffset = regularItems.length > 0
      ? Math.max(...regularItems.map(item => item.index ?? 0)) + 1
      : 0;
    
    const regularFolderIds = new Set(sortedRegularFolders.map(f => f.id));
    const wpFolders = sortedFolders.filter(f => !regularFolderIds.has(f.id));
    
    const pagesWithIndices = pages.filter(p => p.index !== undefined);
    if (pagesWithIndices.length === 0) {
      return pages.map(p => p);
    }
    
    const sortedPages = this._sortPagesByIndex([...pagesWithIndices]);
    const getBaseIndex = ( page, i ) => regularOffset + i;//page unused in this function but is in the _convertRegularPageIndicesToCombined function
    const result = this._reindexItemsWithInsertions(sortedPages, wpFolders, getBaseIndex, false); //false is using folder index for comparison
    
    return pages.map(page => {
      return page.index === undefined || !page.id ? page : result.get(page.id) ?? page;
    });
  },

  /**
   * Sorts folders by index
   * @param {Array} folders - The folders to sort
   * @returns {Array} The sorted folders
   */
  _sortFoldersByIndex(folders) {
    return folders.sort((a, b) => (a.index ?? Infinity) - (b.index ?? Infinity));
  },

  /**
   * Reconstructs folder structure from raw designData (site.json)
   * @param {Object} designData - The raw design data containing items and folders
   * @returns {Object} Object containing regularPages and wpPages arrays with folder structure
   */
  _reconstructFolderStructureFromRaw(designData) {
    const { items, folders, pages = [] } = designData;
    const { regularPages: folderRegularPages, wpPages: folderWpPages } = this._separateFoldersByPageType(folders, items);
    const { regularPages: pageRegularPages, wpPages: pageWpPages } = this._separatePagesByPageType(items, pages);
    
    const sortedRegularFolders = this._sortFoldersByIndex([...folderRegularPages]);
    const sortedWpFolders = this._sortFoldersByIndex([...folderWpPages]);
    const allSortedFolders = this._sortFoldersByIndex([...sortedRegularFolders, ...sortedWpFolders]);
    
    const convertedRegularPages = this._convertRegularPageIndicesToCombined(pageRegularPages, sortedRegularFolders);
    const convertedWpPages = this._convertWpPageIndicesToCombined(
      pageWpPages, 
      allSortedFolders, 
      convertedRegularPages,
      sortedRegularFolders
    );
    
    const regularPages = [...folderRegularPages, ...convertedRegularPages];
    const wpPages = [...folderWpPages, ...convertedWpPages];
  
    const sortByIndex = (a, b) => (a.index ?? 0) - (b.index ?? 0);
    regularPages.sort(sortByIndex);
    wpPages.sort(sortByIndex);
  
    return { regularPages, wpPages };
  },

  _buildPageItems(designItem, pages, type) {
    return pages.map((page) => {
      return this._buildPageItem(designItem, page, type);
    });
  },

  /**
   * Builds a page or folder item with preserved properties
   * @param {Object} designItem - The design item containing id and key
   * @param {Object} page - The page object to build (can be a regular page or folder)
   * @param {string} type - The page type constant (only used if page is not a folder)
   * @returns {Object} Formatted page or folder item with properties: id, name, type, site_id, key, and optionally pId, slug, wp, home, index
   */
  _buildPageItem(designItem, page, type) {
    const isFolder = page.type === ITEM_TYPE_PAGE_FOLDER;

    const pageItem = {
      id: page.id,
      name: page.name,
      type: isFolder ? ITEM_TYPE_PAGE_FOLDER : type,
      site_id: designItem.id,
      key: isFolder
        ? `${designItem.key}-folder-${page.id}`
        : `${designItem.key}/pages/${page.id}`,
    };

    const optionalProps = ["pId", "slug", "wp", "home", "index"];
    optionalProps.forEach((prop) => {
      if (page[prop] !== undefined) {
        pageItem[prop] = page[prop];
      }
    });

    return pageItem;
  },

  _buildSiteBlockItems(designItem, blocks) {
    return _.map(blocks, function (block) {
      return {
        id: block.id,
        name: block.name,
        type: DESIGN_TYPE_SITE_BLOCK,
        site_id: designItem.id,
      };
    });
  },

  _buildPageBlockItems(pageItem, blocks) {
    return _.map(blocks, function (block) {
      const usesSB = BLOCK_PROP_PARENT_BLOCK_ID in block;
      return {
        id: block.id,
        name: block.name,
        type: DESIGN_TYPE_BLOCK,
        site_id: pageItem.site_id,
        page_id: pageItem.page_id,
        uses_site_block: usesSB,
      };
    });
  },

  _setPreviewData(type, id) {
    var previewData = DesignBrowserStore.getDesignItemPreviewData({ type: type, id: id });
    this.state.preview = previewData;
  },

  _updateActionState() {
    if (this.state.restoreMode) {
      this.state.actionEnabled = true;
      return;
    }

    var designType = this.state.designType;
    var selected = this.state.designList.selected;

    switch (designType) {
      case DESIGN_TYPE_BLOCK:
      case DESIGN_TYPE_SITE_BLOCK:
        this.state.actionEnabled = _.has(selected, "block_id") || _.has(selected, "site_block_id");

        break;
      case DESIGN_TYPE_PAGE:
      case DESIGN_TYPE_WP_PAGE:
        this.state.actionEnabled = _.has(selected, "page_id");
        break;
    }
  },

  _reset() {
    this._resetSiteListStoreListener();

    this.loadingItem = null;

    this.state = _.cloneDeep(initialState);
    this.state.designList = _.cloneDeep(designListModel);
  },

  getState() {
    return this.state;
  },

  onGetSiteBackupsComplete(backups) {
    backups.forEach((backup) => this.state.designList.items.push(backup));
    this.trigger(this.state);
  },
});

export default DesignChooserStore;
